import type { StripeConnectInstance } from "@stripe/connect-js";
import { loadConnectAndInitialize } from "@stripe/connect-js/pure";
import { useEffect, useState } from "react";

export interface UseStripeConnectInstanceOptions {
  publishableKey: string;
  /**
   * Initial AccountSession client_secret rendered server-side. Consumed on
   * the first `fetchClientSecret` call; subsequent calls go through
   * `fetchRefreshedClientSecret` so Stripe's runtime can refresh the token
   * before it expires.
   */
  initialClientSecret: string;
  fetchRefreshedClientSecret: () => Promise<string>;
}

/**
 * Initializes Stripe Connect Embedded Components on mount and returns the
 * runtime instance once ready. Lives in product-ui because the appearance +
 * fonts wiring is shared between every embedded surface, but the data
 * fetching for token refresh is delegated to the caller (a page or hook in
 * the app layer) — UI components must not fetch directly.
 *
 * Stripe's `connect-js` script can only run in a real browser (it injects a
 * `<script>` tag into the document head), so initialization is deferred to
 * `useEffect` and `null` is returned during SSR / before mount.
 */
export function useStripeConnectInstance({
  fetchRefreshedClientSecret,
  initialClientSecret,
  publishableKey,
}: UseStripeConnectInstanceOptions): StripeConnectInstance | null {
  const [stripeConnectInstance, setStripeConnectInstance] = useState<StripeConnectInstance | null>(null);

  useEffect(() => {
    let initialSecret: string | null = initialClientSecret;
    const instance = loadConnectAndInitialize({
      publishableKey,
      fetchClientSecret: async () => {
        if (initialSecret) {
          const consumed = initialSecret;
          initialSecret = null;
          return consumed;
        }
        return fetchRefreshedClientSecret();
      },
      fonts: [
        {
          cssSrc: "https://fonts.googleapis.com/css2?family=Rethink+Sans:ital,wght@0,400..800;1,400..800&display=swap",
        },
      ],
      appearance: {
        overlays: "drawer",
        variables: {
          fontFamily: "'Rethink Sans', -apple-system, 'Segoe UI', sans-serif",
          colorPrimary: cssVar("--color-green-500", "#15803d"),
          colorBackground: cssVar("--color-white", "#ffffff"),
          colorText: cssVar("--color-neutral-900", "#0f172a"),
          colorSecondaryText: cssVar("--color-neutral-500", "#64748b"),
          colorBorder: cssVar("--color-neutral-200", "#e2e8f0"),
          colorDanger: cssVar("--color-red-500", "#ef4444"),
          buttonBorderRadius: "12px",
          formBorderRadius: "12px",
          badgeBorderRadius: "9999px",
          overlayBorderRadius: "16px",
          spacingUnit: "4px",
          fontSizeBase: "16px",
        },
      },
    });
    setStripeConnectInstance(instance);
  }, [initialClientSecret, publishableKey, fetchRefreshedClientSecret]);

  return stripeConnectInstance;
}

function cssVar(name: string, fallback: string): string {
  if (typeof document === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}
